import React, { useCallback, useEffect, useState } from "react";
import { Ban, Download, Lock, Mail, Pencil, RefreshCw, Save, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { EDITABLE, KindBadge, StatusBadge, fmt } from "./sesStatus";

function Meta({ label, value, mono }) {
  if (!value) return null;
  return <div><p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</p><p className={`text-sm text-foreground break-all ${mono ? "font-mono" : ""}`}>{value}</p></div>;
}

export default function SesFileDialog({ id, cfg, mailFirst, onClose, onChanged }) {
  const { t, lang } = useLang();
  const [m, setM] = useState(null);
  const [edit, setEdit] = useState(false);
  const [xml, setXml] = useState("");
  const [mail, setMail] = useState(mailFirst);
  const [to, setTo] = useState(cfg.mail_to || "");
  const [mailLang, setMailLang] = useState("es");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/interfaces/ses/messages/${id}`, { params: { lang } }); setM(data); setXml(data.xml); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); onClose(); }
  }, [id, lang, onClose]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok = "saved") => {
    setBusy(true);
    try { const r = await fn(); toast.success(t(ok)); await load(); onChanged?.(); return r; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveXml = () => act(() => api.put(`/interfaces/ses/messages/${id}/xml`, { xml })).then(() => setEdit(false));
  const send = () => act(() => api.post(`/interfaces/ses/messages/${id}/send`), "ses_send");
  const rebuild = () => act(async () => { const { data } = await api.post(`/interfaces/ses/messages/${id}/rebuild`); onClose(); return data; }, "ses_rebuild");
  const cancel = () => act(() => api.post(`/interfaces/ses/messages/${id}/cancel`), "saved");
  const sendMail = () => act(() => api.post(`/interfaces/ses/messages/${id}/mail`, { to, lang: mailLang, note }), "ses_mailed").then(() => setMail(false));
  const download = () => {
    const blob = new Blob([m.xml], { type: "application/xml" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: m.filename });
    a.click(); URL.revokeObjectURL(a.href);
  };

  if (!m) return null;
  const editable = EDITABLE.includes(m.status);
  const canSend = cfg.enabled && (m.status === "ready" || m.status === "rejected");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="ses-file-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 font-display">
            <KindBadge kind={m.kind} /><span className="font-mono text-sm break-all">{m.filename}</span><StatusBadge status={m.status} t={t} />
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-foreground" data-testid="ses-file-description">{m.description}</p>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 rounded-xl border border-border bg-muted/30 p-3">
          <Meta label={t("ses_version")} value={`v${m.version}`} mono />
          <Meta label={t("ses_col_created")} value={`${fmt(m.created_at)} · ${m.created_by}`} />
          <Meta label={t("ses_deadline")} value={fmt(m.deadline)} />
          <Meta label={t("ses_batch")} value={m.batch_id} mono />
          <Meta label={t("ses_codigo")} value={m.codigo_comunicacion} mono />
          <Meta label={t("ses_error")} value={m.error} mono />
          {m.mailed?.length > 0 && <Meta label={t("ses_mailed")} value={m.mailed.map((x) => `${x.to} (${fmt(x.at)})`).join(", ")} />}
        </div>

        {m.missing?.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200" data-testid="ses-missing">
            <p className="font-semibold mb-1">{t("ses_missing")}</p>
            <ul className="list-disc pl-4 space-y-0.5">{m.missing.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </div>
        )}

        {mail ? (
          <div className="rounded-xl border border-border p-4 space-y-3" data-testid="ses-mail-form">
            <div className="grid gap-3 sm:grid-cols-[1fr_10rem]">
              <div className="space-y-1.5"><Label className="text-xs">{t("ses_mail_to_label")}</Label><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ses-mail-to" /></div>
              <div className="space-y-1.5"><Label className="text-xs">{t("ses_mail_lang")}</Label>
                <div className="flex rounded-lg border border-border p-0.5">{["es", "nl", "en"].map((l) => <button key={l} type="button" onClick={() => setMailLang(l)} data-testid={`ses-mail-lang-${l}`} className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold uppercase ${mailLang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>)}</div>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">{t("ses_mail_note")}</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="ses-mail-note" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setMail(false)} data-testid="ses-mail-cancel">{t("cancel")}</Button>
              <Button onClick={sendMail} disabled={busy || !to} data-testid="ses-mail-submit"><Mail className="h-4 w-4 mr-1" />{t("ses_mail_send")}</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t("ses_xml")}</p>
              {!editable && <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" />{t("ses_immutable")}</span>}
            </div>
            {edit ? (
              <Textarea value={xml} onChange={(e) => setXml(e.target.value)} rows={18} className="font-mono text-xs leading-relaxed" data-testid="ses-xml-editor" />
            ) : (
              <pre className="max-h-[40vh] overflow-auto rounded-xl bg-[#0f172a] p-4 font-mono text-xs leading-relaxed text-emerald-100 cb-scroll" data-testid="ses-xml-view">{m.xml}</pre>
            )}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={download} data-testid="ses-download"><Download className="h-4 w-4 mr-1" />{t("ses_download")}</Button>
            {!mail && <Button variant="outline" size="sm" onClick={() => setMail(true)} data-testid="ses-mail-open"><Mail className="h-4 w-4 mr-1" />{t("ses_mail")}</Button>}
            {editable && !edit && <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="ses-edit"><Pencil className="h-4 w-4 mr-1" />{t("ses_edit")}</Button>}
            {edit && <Button size="sm" onClick={saveXml} disabled={busy} data-testid="ses-save-xml"><Save className="h-4 w-4 mr-1" />{t("ses_save_xml")}</Button>}
            {editable && m.kind !== "B" && <Button variant="outline" size="sm" onClick={rebuild} disabled={busy} data-testid="ses-rebuild"><RefreshCw className="h-4 w-4 mr-1" />{t("ses_rebuild")}</Button>}
          </div>
          <div className="flex flex-wrap gap-2">
            {editable && <Button variant="ghost" size="sm" onClick={cancel} disabled={busy} data-testid="ses-withdraw"><Undo2 className="h-4 w-4 mr-1" />{t("ses_withdraw")}</Button>}
            {m.status === "accepted" && m.codigo_comunicacion && <Button variant="destructive" size="sm" onClick={cancel} disabled={busy} data-testid="ses-cancel"><Ban className="h-4 w-4 mr-1" />{t("ses_cancel")}</Button>}
            {canSend && <Button size="sm" onClick={send} disabled={busy} data-testid="ses-send"><Send className="h-4 w-4 mr-1" />{m.status === "rejected" ? t("ses_resend") : t("ses_send")}</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
